import { Fragment, useState } from 'react'
import { Link } from '@tanstack/react-router'
import type { LunarBalancesResponseDto } from '@lunary/api-client'
import { HBars } from '../../shared/ui/charts/hbars'
import { formatNumber } from '../../shared/ui/charts/scale'
import { PersonCard } from '../arcana-readings/person-card'
import { userTitle } from '../users/identity'
import { FLAG_LABELS, formatDateTime, shortId } from './labels'
import { BALANCES_PAGE, useLunarBalances } from './queries'
import styles from './lunars.module.css'

function Distribution({ data }: { data: LunarBalancesResponseDto }) {
  const rows: ReadonlyArray<[string, number]> = [
    ['Медиана', data.median],
    ['90-й процентиль', data.p90],
    ['99-й процентиль', data.p99],
    ['Крупный баланс от', data.alert_balance],
    ['Бесплатных на балансе от', data.alert_free_balance],
  ]
  return (
    <div className={styles.split}>
      <div className="card-panel">
        <p className={styles.caption}>Пользователей по размеру баланса</p>
        <HBars
          label="Распределение балансов"
          color="var(--series-4)"
          items={data.buckets.map((b) => ({ key: b.label, label: b.label, value: b.users }))}
        />
      </div>
      <div className="card-panel">
        <p className={styles.caption}>Процентили и пороги</p>
        {rows.map(([label, value]) => (
          <div key={label} className={styles.line}>
            <span>{label}</span>
            <b>{formatNumber(value)}</b>
          </div>
        ))}
      </div>
    </div>
  )
}

export function BalancesTab() {
  const [flagged, setFlagged] = useState(true)
  const [offset, setOffset] = useState(0)
  const [open, setOpen] = useState<string | null>(null)
  const q = useLunarBalances(flagged, offset)

  return (
    <div className={styles.stack}>
      {q.isPending && <div className="spinner" aria-label="Загрузка" />}
      {q.isError && <p className="error-box">Не удалось загрузить балансы.</p>}
      {q.data && <Distribution data={q.data} />}
      {q.data && (
        <div className="card-panel">
          <div className={styles.head} style={{ marginBottom: 8 }}>
            <p className={styles.caption} style={{ margin: 0 }}>
              Балансы по убыванию. Нажмите на строку, чтобы увидеть сведения о человеке
            </p>
            <div className={styles.periods} role="radiogroup" aria-label="Фильтр">
              {[true, false].map((f) => (
                <button
                  key={String(f)}
                  role="radio"
                  aria-checked={flagged === f}
                  className={flagged === f ? styles.periodActive : styles.period}
                  onClick={() => {
                    setFlagged(f)
                    setOffset(0)
                  }}
                >
                  {f ? 'С флагом' : 'Все'}
                </button>
              ))}
            </div>
          </div>
          <div className={styles.scroll}>
            <table className="users-table">
              <thead>
                <tr>
                  <th>Пользователь</th>
                  <th>Баланс</th>
                  <th>Куплено</th>
                  <th>Бесплатно</th>
                  <th>Бесплатно за 7 дней</th>
                  <th>Последнее движение</th>
                  <th>Флаг</th>
                </tr>
              </thead>
              <tbody>
                {q.data.items.map((r) => {
                  const expanded = open === r.user_id
                  return (
                    <Fragment key={r.user_id}>
                      <tr>
                        <td>
                          <button
                            className="link"
                            aria-expanded={expanded}
                            onClick={() => setOpen(expanded ? null : r.user_id)}
                          >
                            {r.person ? userTitle(r.person).title : shortId(r.user_id)}
                          </button>
                        </td>
                        <td>
                          <b>{formatNumber(r.balance)}</b>
                        </td>
                        <td>{formatNumber(r.paid)}</td>
                        <td>{formatNumber(r.free)}</td>
                        <td>+{formatNumber(r.free_7d)}</td>
                        <td>{formatDateTime(r.last_at)}</td>
                        <td>
                          {r.flags.length === 0 ? (
                            <span className="muted">норма</span>
                          ) : (
                            r.flags.map((f) => (
                              <span key={f} className={f === 'large' ? 'badge' : 'badge danger'}>
                                {FLAG_LABELS[f] ?? f}
                              </span>
                            ))
                          )}
                        </td>
                      </tr>
                      {expanded && (
                        <tr>
                          <td colSpan={7}>
                            {r.person ? (
                              <PersonCard person={r.person} />
                            ) : (
                              <Link to="/users/$userId" params={{ userId: r.user_id }}>
                                Открыть в «Пользователях»
                              </Link>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  )
                })}
                {q.data.items.length === 0 && (
                  <tr>
                    <td colSpan={7} className="muted">
                      Подходящих балансов нет.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className={styles.pager}>
            <button className="secondary" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - BALANCES_PAGE))}>
              Назад
            </button>
            <button className="secondary" disabled={offset + BALANCES_PAGE >= q.data.total} onClick={() => setOffset(offset + BALANCES_PAGE)}>
              Дальше
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
